import * as React from 'react'
import { MoreHorizontal, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

/**
 * A tab bar that always occupies exactly one row.
 *
 * WHY NOT A FIXED COLUMN GRID. The Recovery Manager bar was `grid-cols-8` holding nine tabs, so
 * the ninth wrapped. The defect is not the number — it is that the number had to be hand-edited
 * every time a tab was added, and nobody remembers. Tabs here size to their own labels and the
 * count is derived, so a tenth tab needs no arithmetic from anyone.
 *
 * WHY MEASURE INSTEAD OF ESTIMATING. Label widths are not a function of character count: the
 * fonts differ, the icons differ, and the same screen carries "Map" and "Acompanhamento de
 * Locais" depending on the language. Guessing from string length produces a bar that fits in
 * English and clips in Portuguese, which is the failure this component exists to prevent.
 *
 * HOW. A hidden ghost row holds every tab at natural width and is measured; the real row is then
 * rendered with however many fit, and the remainder go into a "More" menu. The ghost is what
 * makes this stable — measuring the real row would feed its own result back in, hiding a tab,
 * freeing space, showing it again, and oscillating forever.
 */

export interface OverflowTabItem {
  value: string
  label: string
  icon?: React.ReactNode
}

/** Room kept for the "More" button whenever there is anything to put in it. */
const MORE_WIDTH = 52
/** TabsList's own p-1 either side, so the measurement matches what the row can actually use. */
const LIST_PADDING = 8

export function OverflowTabsList({ items, value, onValueChange, className }: {
  items: OverflowTabItem[]
  value: string
  onValueChange: (v: string) => void
  className?: string
}) {
  const { t } = useTranslation()
  const hostRef = React.useRef<HTMLDivElement>(null)
  const ghostRef = React.useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = React.useState(items.length)

  React.useLayoutEffect(() => {
    const host = hostRef.current
    const ghost = ghostRef.current
    if (!host || !ghost) return

    const measure = () => {
      const widths = Array.from(ghost.children).map((c) => (c as HTMLElement).offsetWidth)
      if (widths.length === 0) return
      const available = host.clientWidth - LIST_PADDING
      // Everything fits: no More button, so its width is not reserved.
      const total = widths.reduce((a, b) => a + b, 0)
      if (total <= available) { setVisibleCount(widths.length); return }
      let used = 0
      let n = 0
      for (const w of widths) {
        if (used + w > available - MORE_WIDTH) break
        used += w
        n++
      }
      // At least one tab stays inline; a bar that is nothing but a More button is not a tab bar.
      setVisibleCount(Math.max(n, 1))
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(host)
    // Labels arrive with the language, so a locale switch has to re-measure as surely as a resize.
    ro.observe(ghost)
    return () => ro.disconnect()
  }, [items])

  const visible = items.slice(0, visibleCount)
  const overflow = items.slice(visibleCount)
  const activeIsHidden = overflow.some((i) => i.value === value)

  return (
    <div ref={hostRef} className={cn('relative w-full', className)}>
      {/* The ghost. Measured, never seen, never focusable. */}
      <div
        ref={ghostRef}
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 flex h-10 items-center gap-0 opacity-0"
        style={{ visibility: 'hidden' }}
      >
        {items.map((item) => (
          <span key={item.value} className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-sm font-medium">
            {item.icon}
            {item.label}
          </span>
        ))}
      </div>

      <TabsList className="flex w-full items-center justify-start overflow-hidden">
        {visible.map((item) => (
          <TabsTrigger key={item.value} value={item.value} className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
            {item.icon}
            {item.label}
          </TabsTrigger>
        ))}

        {overflow.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                // Highlighted when the current tab is one of the hidden ones, so the bar never
                // looks as though nothing is selected.
                className={cn(
                  'ml-auto inline-flex h-8 shrink-0 items-center gap-1 rounded-sm px-2 text-sm font-medium transition-colors hover:bg-background/60',
                  activeIsHidden ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                )}
                aria-label={t('tabs.more', 'More tabs')}
                title={t('tabs.more', 'More tabs')}
              >
                <MoreHorizontal className="h-4 w-4" />
                {t('tabs.moreCount', '{{count}} more', { count: overflow.length })}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              {overflow.map((item) => (
                <DropdownMenuItem
                  key={item.value}
                  onSelect={() => onValueChange(item.value)}
                  className="flex items-center gap-2"
                >
                  {item.icon}
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.value === value && <Check className="h-3.5 w-3.5 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TabsList>
    </div>
  )
}
